import {
  linesIntersect,
  StateNode,
  TLClickEventInfo,
  TLPointerEventInfo,
  TLShapeId,
  Vec,
} from "tldraw"
import {
  CustomDrawShape,
  customDrawShapeProps,
  CustomDrawShapeProps,
} from "../../shared/CustomDrawShape"
import { getStrokePoints } from "../shapes/shared/freehand/getStrokePoints"
import { indexOf } from "lodash"

function f(x: number, width: number = 20): number {
  const absX = Math.abs(x)
  if (absX > width) return 0
  return 0.5 * (1 + Math.cos((Math.PI * absX) / width))
}

function quarterCosFalloff(x: number, width = 20): number {
  if (x < 0 || x > width) return 0
  return Math.cos((Math.PI * x) / (2 * width))
}

type Point = { x: number; y: number }
type Vec2 = { x: number; y: number; z: number }
type VecWithDistance = Vec2 & { distanceFromPoint: number }
type Segment = { type: string; points: Point[] }

function simplifyAroundPointAlternating(
  points: Point[],
  target: Point,
  range: number = 4,
): Point[] {
  const index = points.findIndex((p) => p.x === target.x && p.y === target.y)
  if (index === -1) return points // target not found

  const result: Point[] = []

  for (let i = 0; i < points.length; i++) {
    const offset = i - index
    const inRange = Math.abs(offset) <= range

    if (!inRange) {
      // Outside the range, always keep
      result.push(points[i])
    } else if (offset === 0) {
      // Always keep the target
      result.push(points[i])
    } else {
      // Alternate removing neighbors
      const keep = Math.abs(offset) % 2 === 0
      if (keep) result.push(points[i])
    }
  }

  return result
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

// t is the percentage you have made it from one point to the other
export function pointBetweenAt(a: Vec2, b: Vec2, t: number): Vec2 {
  const vec = {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  } as Vec2

  return vec
}

export function densifyLine(line: Vec2[], step: number = 7): Vec2[] {
  if (line.length < 2) return [...line]

  const result: Vec2[] = []

  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i]
    const b = line[i + 1]
    result.push(a) // always include the start of the segment

    const segLen = distance(a, b)
    const count = Math.floor(segLen / step)

    for (let j = 1; j < count; j++) {
      const t = j / count

      result.push(pointBetweenAt(a, b, t))
    }
  }

  result.push(line[line.length - 1]) // include last point

  return result
}

function annotateDistances(line: Vec2[], pointOnLine: Vec2): VecWithDistance[] {
  const index = line.findIndex(
    (p) => p.x === pointOnLine.x && p.y === pointOnLine.y,
  )
  if (index === -1) return []

  const result: VecWithDistance[] = []

  // Backward: from reference point to start
  let dist = 0
  for (let i = index; i > 0; i--) {
    const d = distance(line[i], line[i - 1])
    dist -= d
    result[i - 1] = { ...line[i - 1], distanceFromPoint: dist }
  }

  // Set reference point
  result[index] = { ...line[index], distanceFromPoint: 0 }

  // Forward: from reference point to end
  dist = 0
  for (let i = index; i < line.length - 1; i++) {
    const d = distance(line[i], line[i + 1])
    dist += d
    result[i + 1] = { ...line[i + 1], distanceFromPoint: dist }
  }

  return result
}
function annotateDistancesClosed(
  line: Vec2[],
  pointOnLine: Vec2,
  tolerance = 1e-6,
): VecWithDistance[] {
  const len = line.length
  if (len < 2) return []

  // Helper: check approximate equality
  const isSame = (a: Vec2, b: Vec2) =>
    Math.abs(a.x - b.x) < tolerance && Math.abs(a.y - b.y) < tolerance

  // Find index of pointOnLine
  const index = line.findIndex((p) => isSame(p, pointOnLine))
  if (index === -1) return []

  const result: VecWithDistance[] = new Array(len)

  // Compute cumulative distances in both directions
  const forwardDistances: number[] = new Array(len).fill(0)
  const backwardDistances: number[] = new Array(len).fill(0)

  // Forward: index -> index + 1 ... wrapping
  let dist = 0
  for (let i = 1; i < len; i++) {
    const from = (index + i - 1) % len
    const to = (index + i) % len
    dist += distance(line[from], line[to])
    forwardDistances[to] = dist
  }

  // Backward: index -> index - 1 ... wrapping
  dist = 0
  for (let i = 1; i < len; i++) {
    const from = (index - i + 1 + len) % len
    const to = (index - i + len) % len
    dist += distance(line[from], line[to])
    backwardDistances[to] = dist
  }

  // Choose the shorter of the two distances for each point
  for (let i = 0; i < len; i++) {
    const minDist =
      i === index ? 0 : Math.min(forwardDistances[i], backwardDistances[i])
    result[i] = { ...line[i], distanceFromPoint: minDist }
  }

  return result
}

export class CustomNudge extends StateNode {
  static override id = "nudge"

  isEndPoint = false
  segmentIndex = 0

  info = {} as TLPointerEventInfo
  startPoint = { x: 0, y: 0, z: 0 } as Vec2

  isPen = false
  isPenOrStylus = false
  shapeId = null as null | TLShapeId

  markId = null as null | string

  override onPointerMove(info: TLPointerEventInfo): void {
    if (!this.shapeId) return
    this.info = info
    const zoomLevel = this.editor.getZoomLevel()

    const shape = this.editor.getShape(this.shapeId!)
    if (shape?.type !== "custom-draw") return
    const scaledPoint = this.editor.screenToPage(this.info.point)

    const scaledPoint2 = new Vec(scaledPoint.x, scaledPoint.y)
    const shapePoint = new Vec(shape!.x, shape!.y)

    const newPoint = {
      x: (shape ? scaledPoint2.sub(shapePoint!) : null)!.x,
      y: scaledPoint2!.y,
      z: 0,
    }

    const movementVector = new Vec(
      newPoint.x - this.startPoint.x,
      newPoint.y - this.startPoint.y,
    )
    const segments = (shape?.props as CustomDrawShapeProps).segments
    const segmentLines = segments.map((segment) => {
      return segment.points
    })

    const isFill = (
      this.editor.getCurrentPage()?.meta?.layers as
        | Record<string, any>
        | undefined
    )?.[(shape?.props as CustomDrawShapeProps).layer as string]
      .isFill as boolean

    const distanceLine = isFill
      ? annotateDistancesClosed(
          segmentLines[this.segmentIndex].map((point) => ({
            x: point.x,
            y: point.y,
            z: point.z ?? 0,
          })),
          this.startPoint,
        )
      : annotateDistances(
          segmentLines[this.segmentIndex].map((point) => ({
            x: point.x,
            y: point.y,
            z: point.z ?? 0,
          })),
          this.startPoint,
        )

    const cameraZoom = this.editor.getCamera().z

    this.startPoint = newPoint

    const newLine = densifyLine(
      distanceLine.map((point) => {
        if (point.distanceFromPoint > 3000 / this.editor.getZoomLevel()) {
          return { x: point.x, y: point.y, z: point.z }
        }

        if (point.distanceFromPoint < -3000 / this.editor.getZoomLevel()) {
          return { x: point.x, y: point.y, z: point.z }
        }

        return {
          x:
            point.x +
            movementVector.x *
              (this.isEndPoint
                ? quarterCosFalloff(
                    -Math.abs(point.distanceFromPoint),
                    400 / cameraZoom,
                  )
                : f(point.distanceFromPoint, 170 / cameraZoom)),

          y:
            point.y +
            movementVector.y *
              (this.isEndPoint
                ? quarterCosFalloff(
                    -Math.abs(point.distanceFromPoint),
                    400 / cameraZoom,
                  )
                : f(point.distanceFromPoint, 170 / cameraZoom)),
          z: point.z,
        } as Vec2
      }),
    )

    var newNewLine

    //if we are really zoomed in lets not apply smoothing
    if (
      this.editor.getZoomLevel() < 1.2 ||
      (isFill == false && this.isEndPoint)
    ) {
      if (this.editor.getZoomLevel() < 0.8) {
        newNewLine = simplifyAroundPointAlternating(newLine, this.startPoint, 5)
      } else {
        newNewLine = simplifyAroundPointAlternating(newLine, this.startPoint, 1)
      }
    } else {
      newNewLine = newLine
    }
    if (newNewLine.length < 1) {
      console.log("cooked")
      return
    }

    const updatedSegments = [...segments]
    updatedSegments[this.segmentIndex] = { type: "free", points: newNewLine }

    this.editor.updateShape({
      id: this.shapeId!,
      props: {
        segments: updatedSegments.map((segment) => {
          return {
            type: segment.type,
            points: segment.points.map((point) => ({
              x: point.x,
              y: point.y,
            })),
          }
        }),
      },
      type: "custom-draw",
    })
  }

  override onEnter(info: TLPointerEventInfo): void {
    this.info = info

    const scaledPoint = this.editor.screenToPage(this.info.point)

    const distances = [] as number[]

    type ClosestPoint = {
      segmentIndex: number
      position: number
      x: number
      y: number
      distance: number
      shapeId: null | TLShapeId
      scaledX?: number
      scaledY?: number
    }
    const closestPoint: ClosestPoint = Array.from(
      this.editor.getCurrentPageShapeIds(),
    ).reduce<ClosestPoint>(
      (closest: ClosestPoint, shapeId) => {
        const shape = this.editor.getShape(shapeId) as CustomDrawShape
        if (shape.type !== "custom-draw") return closest

        // whenever directly modifying the shapes points we need to use the .segments property because it has the points pre-smoothing
        const segmentPoints = shape.props.segments.map((segment) => {
          return segment.points
        })

        // Convert the point to the shape's local coordinate space

        const scaledPoint2 = new Vec(scaledPoint.x, scaledPoint.y)

        const shapePoint = new Vec(shape!.x, shape!.y)

        const relativePoint = shape ? scaledPoint2.sub(shapePoint!) : null

        // const layer = (shape?.meta.layer)
        // layer && (layer as TLShapeId)
        //console.log("layer", shape)

        //if (shape?.props.layer?.toString().includes("fill")) return closest

        if (shape?.props.layer?.toString().includes("fill")) {
          if (this.editor.getBindingsInvolvingShape(shape).length > 0) {
            //console.log("shape has fill binding", shape.id)
            return closest
          }
        }

        if (shape?.type !== "custom-draw") return closest

        if (!shape || !relativePoint) return closest

        segmentPoints.forEach((points, index2) => {
          points.forEach((vertex, index) => {
            const distance = Math.sqrt(
              Math.pow(vertex.x - relativePoint.x, 2) +
                Math.pow(vertex.y - relativePoint.y, 2),
            )
            distances.push(distance)

            if (distance < closest.distance) {
              closest = {
                segmentIndex: index2,
                position: index,
                distance,
                shapeId,
                x: vertex.x + shape.x,
                y: vertex.y + shape.y,
                scaledX: vertex.x,
                scaledY: vertex.y,
              }
            }
          })
        })
        return closest
      },
      {
        segmentIndex: -1,
        position: -1,
        x: 0,
        y: 0,
        scaledX: undefined,
        scaledY: undefined,
        distance: Infinity,
        shapeId: null,
      },
    )

    // const page = (this.editor.getCurrentPage() as LDPage).meta

    //const screenPoint = this.editor.screenToPage({ x: closestPoint.x, y: closestPoint.y })

    if (closestPoint.shapeId && closestPoint.distance < 20) {
      const point = {
        x: closestPoint.scaledX!,
        y: closestPoint.scaledY!,
        z: 0,
      } as Vec2
      this.segmentIndex = closestPoint.segmentIndex

      this.startPoint = point
      this.shapeId = closestPoint.shapeId as TLShapeId
      const shape = this.editor.getShape(closestPoint.shapeId!)
      const shapePointsLength = (shape?.props as CustomDrawShapeProps)
        .segments[0].points.length
      const isFill = (
        this.editor.getCurrentPage()?.meta?.layers as
          | Record<string, any>
          | undefined
      )?.[(shape?.props as CustomDrawShapeProps).layer as string]
        .isFill as boolean

      if (
        (!isFill && closestPoint.position === 0) ||
        closestPoint.position === shapePointsLength - 1
      ) {
        this.isEndPoint = true
      } else {
        this.isEndPoint = false
      }
    } else {
      this.shapeId = null
    }
  }
  // override onLongPress(info: TLPointerEventInfo): void {
  //   if (this.startPoint.x === 0) {
  //     return
  //   }
  //   const shape = this.editor.getShape(this.shapeId!)
  //   const line = (shape!.props as CustomDrawShapeProps).segments[0].points

  //   const newNewLine = densifyLine(
  //     simplifyAroundPointAlternating(line, this.startPoint, 10).map(
  //       (point) => ({
  //         ...point,
  //         z: 0.5,
  //       }),
  //     ),
  //   )

  //   this.editor.updateShape({
  //     x: shape!.x,
  //     y: shape!.y,

  //     id: this.shapeId!,
  //     props: {
  //       segments: [
  //         {
  //           type: "free",
  //           points: newNewLine,
  //         },
  //       ],
  //     },
  //     type: "custom-draw",
  //   })
  // }

  override onDoubleClick(info: TLClickEventInfo): void {
    if (!this.shapeId) {
      this.editor.setCurrentTool("select")
      return
    }
    const shape = this.editor.getShape(this.shapeId!)
    const line = (shape!.props as CustomDrawShapeProps).segments[0].points

    const newNewLine = densifyLine(
      simplifyAroundPointAlternating(line, this.startPoint, 10).map(
        (point) => ({
          ...point,
          z: 0.5,
        }),
      ),
    )

    // this.editor.updateShape({
    //   x: shape!.x,
    //   y: shape!.y,

    //   id: this.shapeId!,
    //   props: {
    //     segments:

    //      [
    //       {
    //         type: "free",
    //         points: newNewLine,
    //       },
    //     ],
    //   },
    //   type: "custom-draw",
    // })
  }

  override onPointerUp(info: TLPointerEventInfo): void {
    this.parent.transition("nudge-idle", info)
    this.editor.markHistoryStoppingPoint()
  }
}
